// Infrastructure & Costs — the admin's picture of what this instance runs
// on, what it costs, and how to grow it.
//
//   Map      live React Flow diagram (InfraMap) with health + cost per box
//   Costs    estimate per resource / category from the price table, the
//            Cost Explorer actuals (project tag when cost-allocation tags
//            are active, otherwise the whole account), the drift grade, and
//            the existing development/routing ledgers (CostReport)
//   Add-ons  the blueprint catalog priced for THIS instance plus a free-form
//            brief; either opens a Dev Console agent session that writes a
//            CloudFormation stack under infra/ and a PR, and the Merge &
//            Deploy runner's `infra` target applies it. Plans list below.
//
// Rendered as the Dev Console's `infra` view and as the /infra page for
// org admins without the internal claim (reads go through
// GET /dev/ops/infra, requireOpsViewer). Refresh and Plan are internal-only
// controls on the server; the UI hides them for everyone else.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Tabs from '../Tabs';
import InfraMap from './InfraMap';
import { kitEnv } from '../../data/kitEnv.js';
import { getIdToken } from '../../data/authPlane';
import { CostLedgers } from '../../pages/CostReport';
import './InfraMap.css';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
const fmtUsd = (n) => (n == null ? '—' : `$${Number(n).toFixed(n >= 100 ? 0 : 2)}`);
const ago = (ms) => { if (!ms) return '—'; const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
const CATEGORY_LABEL = { edge: 'Edge', network: 'Network', compute: 'Compute', data: 'Data', storage: 'Storage', build: 'Build', security: 'Security', observability: 'Observability', ai: 'AI', dev: 'Dev', other: 'Other' };

async function api(path, body) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${path}: ${res.status}`);
  return json;
}

export function useInfraReport() {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (fresh) => {
    setBusy(true);
    try { setReport(await api(`/dev/ops/infra${fresh ? '?fresh=1' : ''}`)); setErr(null); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => {
    load(false);
    const id = setInterval(() => load(false), 120000);
    return () => clearInterval(id);
  }, [load]);
  return { report, err, busy, reload: load, setReport };
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`cs-stat${tone ? ` t-${tone}` : ''}`}>
      <div className="cs-stat-label">{label}</div>
      <div className="cs-stat-value u-tabular">{value}</div>
      {sub && <div className="u-xs u-muted">{sub}</div>}
    </div>
  );
}

function DriftBanner({ costs, tagKey }) {
  const d = costs?.drift;
  const a = costs?.actual;
  if (!a) return <div className="cs-banner b-info">No Cost Explorer data yet — the cost worker pulls within a minute of backend boot and every 6 hours after.</div>;
  if (a.basis !== 'tag') {
    return (
      <div className="cs-banner b-warn infra-drift">
        <span>Actuals cover the <strong>whole AWS account</strong>. To reconcile this app alone, activate the <code>{tagKey}</code> cost-allocation tag in Billing → Cost allocation tags (runbook: enable-cost-allocation-tags); the worker starts a tag-filtered pull on its own.</span>
      </div>
    );
  }
  if (!d) return null;
  const tone = d.classification === 'aligned' ? 'ok' : d.classification === 'minor' ? 'info' : 'warn';
  return (
    <div className={`cs-banner b-${tone} infra-drift`}>
      <span>Estimate vs bill over {d.days} complete day{d.days === 1 ? '' : 's'}: <strong>{d.driftPct == null ? '—' : `${d.driftPct > 0 ? '+' : ''}${d.driftPct}%`}</strong> ({d.classification})</span>
      <span className="u-muted">estimated {fmtUsd(d.estimatedMtdUsd)} · billed {fmtUsd(d.actualMtdUsd)} · basis <code>{tagKey}</code> tag</span>
    </div>
  );
}

function CostsTab({ report, internal }) {
  const est = report?.costs?.estimate;
  const act = report?.costs?.actual;
  const cats = useMemo(() => Object.entries(est?.byCategory || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]), [est]);
  const max = Math.max(0.01, ...cats.map(([, v]) => v));
  const rows = est?.byResource || [];
  return (
    <div className="infra-panel">
      <DriftBanner costs={report?.costs} tagKey={report?.tagKey} />
      <div className="infra-stats">
        <Stat label="Estimated / month" value={fmtUsd(est?.monthlyUsd)} sub={est ? `$${est.burnPerHourUsd}/h · ${est.hoursElapsed} h into today` : 'no estimate yet'} />
        <Stat label="Estimated today" value={fmtUsd(est?.dailyUsd)} sub="from the price table and live uptime" />
        <Stat label={`Billed MTD · ${act?.basis === 'tag' ? 'this app' : 'account'}`} value={fmtUsd(act?.mtdUsd)} sub={act ? `pulled ${ago(act.pulledAt)}` : 'waiting on Cost Explorer'} />
        <Stat label="Projected month" value={fmtUsd(act?.projectedUsd)} sub={act ? `${fmtUsd(act.burnPerDayUsd)}/day recent burn` : ''} />
      </div>
      <div className="u-bento">
        <section className="cs-card">
          <h3>By category (today)</h3>
          {!cats.length && <div className="u-muted u-small">Nothing billable estimated yet.</div>}
          <div className="infra-bars">
            {cats.map(([k, v]) => (
              <div className="infra-bar" key={k}>
                <span>{CATEGORY_LABEL[k] || k}</span>
                <div className="infra-bar-track"><div className="infra-bar-fill" style={{ width: `${(v / max) * 100}%` }} /></div>
                <span className="u-tabular" style={{ textAlign: 'right' }}>{fmtUsd(v)}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="cs-card">
          <h3>Billed by AWS service (MTD)</h3>
          {!act?.byService || !Object.keys(act.byService).length ? <div className="u-muted u-small">No actuals yet.</div> : (
            <table className="cs-table">
              <tbody>
                {Object.entries(act.byService).slice(0, 12).map(([k, v]) => <tr key={k}><td>{k}</td><td className="u-tabular" style={{ textAlign: 'right' }}>{fmtUsd(v)}</td></tr>)}
              </tbody>
            </table>
          )}
        </section>
      </div>
      <section className="cs-card">
        <h3>Per resource</h3>
        {!rows.length ? <div className="u-muted u-small">No resources carry a cost model yet.</div> : (
          <table className="cs-table">
            <thead><tr><th>Resource</th><th>Category</th><th>Basis</th><th style={{ textAlign: 'right' }}>Today</th><th style={{ textAlign: 'right' }}>/ month</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}{r.addon ? <span className="u-muted"> · {r.addon}</span> : null}{r.source === 'planned' ? <span className="im-tag t-info" style={{ marginLeft: 6 }}>planned</span> : null}</td>
                  <td className="u-muted">{CATEGORY_LABEL[r.category] || r.category}</td>
                  <td className="u-muted u-small">{r.basis}</td>
                  <td className="u-tabular" style={{ textAlign: 'right' }}>{r.basis === 'external' ? 'elsewhere' : fmtUsd(r.dailyUsd)}</td>
                  <td className="u-tabular" style={{ textAlign: 'right' }}>{r.basis === 'external' ? '—' : fmtUsd(r.monthlyUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="u-xs u-muted" style={{ marginTop: 'var(--sp-2)' }}>Prices from <code>infra.pricing</code> in live config (defaults: us-east-1 on-demand). Model providers and the control plane bill elsewhere and are listed, not summed.</div>
      </section>
      {internal && <CostLedgers hosting={false} />}
    </div>
  );
}

function BlueprintCard({ bp, onPlan, canPlan }) {
  const unavailable = !bp.available;
  return (
    <div className={`infra-bp${unavailable ? ' unavailable' : ''}`}>
      <div className="infra-bp-head">
        <span className="infra-bp-name">{bp.name}</span>
        <span className="infra-bp-price">{bp.estMonthlyUsd == null ? 'usage' : bp.estMonthlyUsd === 0 ? 'free' : `+${fmtUsd(bp.estMonthlyUsd)}/mo`}</span>
      </div>
      <div className="infra-bp-sum">{bp.summary}</div>
      {bp.note && <div className="u-xs u-muted">{bp.note}</div>}
      <div className="infra-bp-foot">
        <span className="im-tag">{bp.category}</span>
        {bp.applied && <span className="im-tag t-ok">applied</span>}
        {unavailable && <span className="im-tag t-warn" title={`needs: ${bp.missing.join(', ')}`}>needs {bp.missing.join(', ')}</span>}
        <span className="spacer" style={{ flex: 1 }} />
        {canPlan && !bp.applied && (
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={unavailable} onClick={() => onPlan(bp)}>Plan with the agent</button>
        )}
      </div>
    </div>
  );
}

const PLAN_TONE = { proposed: 'info', planning: 'info', pr: 'info', applying: 'warn', applied: 'ok', failed: 'danger', cancelled: 'off' };

function PlansList({ plans, onOpenSession }) {
  if (!plans?.length) return <div className="u-muted u-small">No infra plans yet. Pick a blueprint or describe what you need above.</div>;
  return (
    <table className="cs-table">
      <thead><tr><th>Plan</th><th>Status</th><th style={{ textAlign: 'right' }}>Est. / month</th><th>When</th><th /></tr></thead>
      <tbody>
        {plans.map((p) => (
          <tr key={p.id}>
            <td>{p.title || (p.blueprints || []).join(', ') || p.brief?.slice(0, 80) || p.id}</td>
            <td><span className={`im-tag t-${PLAN_TONE[p.status] || 'off'}`}>{p.status}</span>{p.error ? <span className="u-xs u-muted" title={p.error}> · {p.error.slice(0, 60)}</span> : null}</td>
            <td className="u-tabular" style={{ textAlign: 'right' }}>{p.estMonthlyUsd == null ? '—' : `+${fmtUsd(p.estMonthlyUsd)}`}</td>
            <td className="u-muted u-small">{ago(p.createdAt)}</td>
            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              {p.prUrl && <a className="cs-btn cs-btn-ghost cs-btn-sm" href={p.prUrl} target="_blank" rel="noreferrer">PR</a>}
              {p.sessionId && onOpenSession && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onOpenSession(p.sessionId)}>Session</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AddonsTab({ report, internal, onOpenSession, reload, prefill }) {
  const [brief, setBrief] = useState(prefill || '');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const toggle = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const estDelta = (report?.blueprints || []).filter((b) => picked.includes(b.key)).reduce((s, b) => s + (b.estMonthlyUsd || 0), 0);
  const submit = async () => {
    if (!picked.length && !brief.trim()) return;
    setBusy(true); setErr(null);
    try {
      const out = await api('/dev/ops/infra/plan', { blueprints: picked, brief: brief.trim() });
      setPicked([]); setBrief('');
      await reload(false);
      if (out.sessionId && onOpenSession) onOpenSession(out.sessionId);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const bps = report?.blueprints || [];
  return (
    <div className="infra-panel">
      {!internal && <div className="cs-banner b-info">Planning infrastructure changes opens a Dev Console agent session, which needs the internal claim. You can review the catalog, its prices for this instance, and the plans below.</div>}
      <section className="cs-card">
        <h3>Blueprints</h3>
        <div className="u-small u-muted" style={{ marginBottom: 'var(--sp-3)' }}>Priced for this instance (region, subnets, load balancer as discovered). Pick one or more, add a note, and the agent writes the CloudFormation stack + add-on manifest under <code>infra/</code> and opens a PR; Merge &amp; Deploy applies it as a change set.</div>
        <div className="infra-grid">
          {bps.map((bp) => (
            <div key={bp.key} onClick={() => internal && bp.available && !bp.applied && toggle(bp.key)} style={{ cursor: internal && bp.available && !bp.applied ? 'pointer' : 'default', outline: picked.includes(bp.key) ? '2px solid var(--info-border)' : 'none', borderRadius: 'var(--r-lg)' }}>
              <BlueprintCard bp={bp} canPlan={false} onPlan={() => {}} />
            </div>
          ))}
          {!bps.length && <div className="u-muted u-small">No blueprints available for this runtime.</div>}
        </div>
      </section>
      {internal && (
        <section className="cs-card infra-ask">
          <h3>Describe what you need</h3>
          <textarea className="cs-input" value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="e.g. a private subnet pair with a NAT so the backend can reach a vendor API from a fixed IP; or: a Postgres for the analytics module, smallest instance, 7-day backups" data-nova-note="Free-form infrastructure brief; the agent turns it into CloudFormation under infra/ and a PR" />
          <div className="infra-bp-foot">
            <span className="u-small u-muted">{picked.length ? `${picked.length} blueprint${picked.length === 1 ? '' : 's'} · about +${fmtUsd(estDelta)}/mo` : 'no blueprint selected'}{brief.trim() ? ' · custom brief' : ''}</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || (!picked.length && !brief.trim())} onClick={submit} data-agent-guard="opens an agent session that writes infrastructure code and a PR">
              {busy ? 'Opening session…' : 'Plan with the agent'}
            </button>
          </div>
          {err && <div className="cs-banner b-danger">{err}</div>}
        </section>
      )}
      <section className="cs-card">
        <h3>Plans</h3>
        <PlansList plans={report?.plans} onOpenSession={onOpenSession} />
      </section>
      {report?.inventory?.addons?.length > 0 && (
        <section className="cs-card">
          <h3>Add-ons in this repo</h3>
          <table className="cs-table">
            <thead><tr><th>Add-on</th><th>Blueprint</th><th>Stack</th><th>Status</th><th style={{ textAlign: 'right' }}>Est. / month</th></tr></thead>
            <tbody>
              {report.inventory.addons.map((a) => (
                <tr key={a.name}><td>{a.name}<div className="u-xs u-muted">{a.summary}</div></td><td className="u-muted">{a.blueprint || 'custom'}</td><td className="u-small"><code>{a.stack}</code></td><td><span className={`im-tag t-${a.applied ? 'ok' : 'info'}`}>{a.applied ? `applied ${ago(a.appliedAt)}` : 'declared'}</span></td><td className="u-tabular" style={{ textAlign: 'right' }}>{a.estMonthlyUsd == null ? '—' : `+${fmtUsd(a.estMonthlyUsd)}`}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

export default function InfraPanel({ internal = false, onOpenSession, initialTab = 'map' }) {
  const { report, err, busy, reload } = useInfraReport();
  // Tabs is uncontrolled; "Plan a change here" from the inspector jumps to
  // Add-ons with a prefilled brief by remounting it (jump counter as key).
  const [tab, setTab] = useState(initialTab);
  const [jump, setJump] = useState(0);
  const [prefill, setPrefill] = useState('');
  const inv = report?.inventory;
  const missing = inv?.drift?.missing || [];
  const planHere = useCallback((r) => {
    setPrefill(`Regarding ${r.name} (${r.type}${r.details?.cidr ? `, ${r.details.cidr}` : ''}): `);
    setTab('addons');
    setJump((n) => n + 1);
  }, []);
  const tabs = [
    { id: 'map', label: 'Map', render: () => <InfraMap inventory={inv} costs={report?.costs} onPlan={internal ? planHere : null} /> },
    { id: 'costs', label: 'Costs', render: () => <CostsTab report={report} internal={internal} /> },
    { id: 'addons', label: `Add-ons${report?.plans?.length ? ` (${report.plans.length})` : ''}`, render: () => <AddonsTab report={report} internal={internal} onOpenSession={onOpenSession} reload={reload} prefill={prefill} /> },
  ];
  return (
    <div className="infra-panel" data-nova-note="Infrastructure & Costs: the live map of this app's cloud resources, their estimated and billed costs, and add-on blueprints the agent can provision">
      <div className="infra-head">
        <h2>Infrastructure &amp; Costs</h2>
        <span className="u-small u-muted">{inv ? `${inv.resources.length} resources · ${inv.discovered} discovered · ${inv.runtime === 'docker' ? 'self-host' : `${inv.region}`} · swept ${ago(inv.checkedAt)}` : busy ? 'loading…' : 'no inventory yet'}</span>
        <span className="spacer" />
        {internal && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => reload(true)} title="Re-walk the account now (the worker does this every infra.discoverEveryMin minutes)">{busy ? 'Sweeping…' : 'Refresh'}</button>}
      </div>
      {err && <div className="cs-banner b-danger">{err}</div>}
      {missing.length > 0 && <div className="cs-banner b-warn">Declared but not found: {missing.join(', ')}. Either the role lacks <code>scripts/iam-infra-read-policy.json</code> or the resource is gone.</div>}
      {inv && Object.keys(inv.probeErrors || {}).length > 0 && <div className="cs-banner b-info">Some probes failed ({Object.keys(inv.probeErrors).join(', ')}) — the map shows what could be read. Grant the read policy for the rest.</div>}
      <div className="infra-stats">
        <Stat label="Estimated / month" value={fmtUsd(report?.costs?.estimate?.monthlyUsd)} sub="price table × live uptime" />
        <Stat label={`Billed MTD · ${report?.costs?.actual?.basis === 'tag' ? 'this app' : report?.costs?.actual ? 'account' : '—'}`} value={fmtUsd(report?.costs?.actual?.mtdUsd)} sub={report?.costs?.actual ? `projected ${fmtUsd(report.costs.actual.projectedUsd)}` : 'Cost Explorer pending'} />
        <Stat label="Health" value={inv ? `${inv.resources.filter((r) => ['running', 'active', 'available', 'ok', 'healthy'].includes(r.liveStatus?.status)).length}/${inv.resources.filter((r) => r.liveStatus && !['external', 'declared'].includes(r.liveStatus.status)).length}` : '—'} sub="live resources reporting healthy" tone={missing.length ? 'warn' : undefined} />
        <Stat label="Add-ons" value={inv ? String((inv.addons || []).length) : '—'} sub={report?.plans?.length ? `${report.plans.filter((p) => !['applied', 'failed', 'cancelled'].includes(p.status)).length} plan(s) in flight` : 'none planned'} />
      </div>
      <Tabs key={jump} tabs={tabs} initial={tab} onChange={setTab} />
    </div>
  );
}
